import React, { Suspense, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { connect } from "react-redux";

// Utilities
import { Group, error } from "../utils/logger";
import { get } from "lodash";

// UI Components
import { useTranslation } from "react-i18next";
import { faUser, faUserLock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Container, Nav, Navbar, NavDropdown, Dropdown } from "react-bootstrap";

// Custom Components
import ModalTenants from "../pages/Auth/Modal.Tenants";

// State
import { CPP_SESSION } from "../state/constants";
import { logoutUser } from "../state/actions/user";
import { getCPPConfig } from "../state/actions/config";
import { clearSearchList } from "../state/actions/search";
import { getCPP, setCPP, refreshCPP } from "../state/actions/cpp";

// Types
import { CPP } from "../types/cpp";

const DISABLE_TENANT_SWITCH = true;
const LoggerGroup = new Group("AppBar");

function Page({
  user,
  cpp,
  logoutUser,
  getCPP,
  setCPP,
  refreshCPP,
  clearSearchList,
  getCPPConfig,
}: any) {
  const history = useHistory();
  const { t } = useTranslation("sample");
  const [currentCppId, setCurrentCppId] = useState();
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [showTenantSwitch, setShowTenantSwitch] = useState(false);
  const [accountPermissions, setAccountPermissions] = useState("");
  const [reportingPermissions, setReportingPermissions] = useState("");
  const [switchboardPermissions, setSwitchboardPermissions] = useState("");
  const [userManagementPermissions, setUserManagementPermissions] = useState("");
  const [fileUploadPermissions, setFileUploadPermissions] = useState("");
  const [showFileUpload, setShowFileUpload] = useState(false);

  useEffect(() => {
    if (cpp.current.id === undefined && cpp.list.length > 0) {
      const sessionEmail = sessionStorage.getItem(CPP_SESSION);
      let found = cpp.list.find((element: CPP) =>
        sessionEmail
          ? sessionEmail.endsWith(element.emailDomain)
          : user.details.email.endsWith(element.emailDomain)
      );

      setCPP(found || cpp.list[0]);
    }
    showHideFileUpload(cpp.current);
  }, [cpp, user.details.email, setCPP]);

  //  TODO: Handle tenant switch access
  useEffect(() => {
    const admin: boolean = get(user, "details.admin");

    if (admin) {
      setShowTenantSwitch(true);
    }
  }, [user, user.details]);

  // Handle permission changes
  useEffect(() => {
    const permissions: string[] = get(user, "details.permissions") || [];

    const account = permissions.find((p) => p.includes("account"));
    if (account) {
      setAccountPermissions(account);
    } else {
      setAccountPermissions("");
    }

    const switchboard = permissions.find((p) => p.includes("switchboard"));
    if (switchboard) {
      setSwitchboardPermissions(switchboard);
    } else {
      setSwitchboardPermissions("");
    }

    const reporting = permissions.find((p) => p.includes("reporting"));
    if (reporting) {
      setReportingPermissions(reporting);
    } else {
      setReportingPermissions("");
    }

    const userManagement = permissions.find((p) => p.includes("userManagement"));
    if (userManagement) {
      setUserManagementPermissions(userManagement);
    } else {
      setUserManagementPermissions("");
    }

    const fileUpload: any = permissions.find((p) => p.includes("fileUpload"));
    if (userManagement) {
      setFileUploadPermissions(fileUpload);
    } else {
      setFileUploadPermissions("");
    }
  }, [user, user.details]);

  // Handle CPP Loading/Changes
  useEffect(() => {
    if (!cpp.loading) {
      const cpp_id = cpp.current.id;

      if (cpp_id === undefined) {
        LoggerGroup.emit(`Initializing: fetching CPP config`);

        getCPP();
        getCPPConfig();
      } else if (currentCppId === undefined) {
        LoggerGroup.emit(`Loaded: CPP[${cpp_id}]`);

        setCurrentCppId(cpp_id);
      } else if (currentCppId !== cpp_id) {
        LoggerGroup.emit(`CPP changed: refreshing cpp config[${cpp_id}]`);

        setCurrentCppId(cpp_id);
        getCPPConfig();
      }
    }
  }, [cpp, currentCppId, getCPP, getCPPConfig]);

  const handleLogOut = () => {
    logoutUser()
      .then(() => history.push("/auth/sign-in"))
      .catch(error);
  };

  const AuthenticationButton = () => {
    // user is logged in
    if (user.isAuthenticated) {
      return <Button onClick={handleLogOut}>{t(`auth.sign-out`)}</Button>;
    }

    return (
      <NavDropdown id="authentication-dropdown" title={t(`auth.nav-bar-label`)} as="div">
        {["sign-in", "register"].map((key) => (
          <NavDropdown.Item key={key} as={Link} to={`/auth/${key}`}>
            {t(`auth.${key}`)}
          </NavDropdown.Item>
        ))}
      </NavDropdown>
    );
  };

  const showHideFileUpload = (cppElement: CPP) => {
    if (cppElement?.key?.toUpperCase() === "NYSEG" || cppElement?.key?.toUpperCase() === "RGE") {
      setShowFileUpload(true);
    } else {
      setShowFileUpload(false);
    }
  };

  const onDropdownClick = (cppElement: CPP) => {
    showHideFileUpload(cppElement);
    refreshCPP(cppElement);
    clearSearchList();
    history.push("/home/dashboard");
  };

  const TenantModal = () => {
    // TODO: WIP
    if (!showTenantSwitch || DISABLE_TENANT_SWITCH) {
      return null;
    }

    return (
      <>
        <Button
          variant="outline-success"
          className="mr-2"
          onClick={() => setShowTenantModal(!showTenantModal)}
        >
          <FontAwesomeIcon icon={faUserLock} className="mr-2" />
          Switch Tenant
        </Button>

        <ModalTenants show={showTenantModal} stayOnCancel={true} toggleModal={setShowTenantModal} />
      </>
    );
  };

  const CPPDropDown = ({ clients }: { clients: CPP[] }) => {
    if (clients.length > 1) {
      return (
        <Dropdown>
          <Dropdown.Toggle
            variant="success"
            id="tenant-switch-dropdown"
            disabled={clients.length === 1}
          >
            {cpp.current.title}
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {clients.map((cppElement: CPP, index: number) => (
              <Dropdown.Item
                key={index}
                onClick={() => {
                  onDropdownClick(cppElement);
                }}
              >
                {cppElement.title}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      );
    }

    return null;
  };

  const onSettingsClick = () => {
    history.push({
      pathname: "/user-management/account-settings",
      state: {
        data: {
          ...user.details,
        },
      },
    });
  };

  return (
    <Suspense fallback={<Loader />}>
      <Navbar bg="dark" variant="dark" expand="lg">
        {/*<Container>*/}
        <Navbar.Brand as={Link} to="/">
          Convey
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="landing-navbar" />

        <Navbar.Collapse id="landing-navbar">
          <Nav className="mr-auto">
            {/* ACCOUNTS */}
            {accountPermissions && (
              <NavDropdown title="Preferences" id="preference-nav-item">
                <NavDropdown.Item as={Link} to="/pref/search">
                  Search Currently Enrolled Customers
                </NavDropdown.Item>

                {accountPermissions.includes("readWrite") && (
                  <NavDropdown.Item as={Link} to="/pref/add">
                    Add New Customer
                  </NavDropdown.Item>
                )}

                {cpp.current.chatBotKey && (
                  <NavDropdown.Item as={Link} to="/preference/keyword-simulator">
                    Keyword Simulator
                  </NavDropdown.Item>
                )}
              </NavDropdown>
            )}

            {/* REPORTING */}
            {reportingPermissions && (
              <NavDropdown title="Reporting" id="reporting-nav-item">
                <NavDropdown.Item as={Link} to="/report/dashboard">
                  Dashboard
                </NavDropdown.Item>

                {reportingPermissions.includes("readWrite") && (
                  <NavDropdown.Item as={Link} to="/report/change">
                    Change Dashboard View
                  </NavDropdown.Item>
                )}
              </NavDropdown>
            )}

            {/* SWITCHBOARD */}
            {switchboardPermissions && (
              <Nav.Link as={Link} to="/switchboard" id="switchboard-nav-item">
                Switchboard
              </Nav.Link>
            )}

            {/* USER MANAGEMENT */}
            {userManagementPermissions && (
              <NavDropdown title="User Management" id="user-management-nav-item">
                <NavDropdown.Item as={Link} to="/user-management/current-users">
                  Current Users
                </NavDropdown.Item>

                {userManagementPermissions.includes("readWrite") && (
                  <NavDropdown.Item as={Link} to="/user-management/add-new-user">
                    Add New User
                  </NavDropdown.Item>
                )}
              </NavDropdown>
            )}

            {fileUploadPermissions && showFileUpload && (
              <NavDropdown title="File upload" id="file-upload-nav-item">
                <NavDropdown.Item as={Link} to="/file/lse">
                  LSE
                </NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/file/lseprestorm">
                  LSE Storm
                </NavDropdown.Item>
              </NavDropdown>
            )}
          </Nav>

          {/* TENANCY EVENTS */}
          <Nav className="ml-auto">
            <TenantModal />

            <CPPDropDown clients={cpp.list} />
          </Nav>

          <Nav className="ml-auto">
            {!user.details.admin ? (
              <Button className="mr-2" onClick={onSettingsClick}>
                <FontAwesomeIcon icon={faUser} />
              </Button>
            ) : null}
            <AuthenticationButton />

            {/* LANGUAGE */}
            {/*<ExampleLanguageChange />*/}

            {/* THEME */}
            {/*<ExampleThemeChange />*/}
          </Nav>
        </Navbar.Collapse>
        {/*</Container>*/}
      </Navbar>
    </Suspense>
  );
}

// loading component for suspense fallback
const Loader = () => (
  <div className="App-NavBar">
    <Navbar bg="dark" variant="dark" expand="lg">
      <Container>
        <Navbar.Brand as={Link} to="/">
          Convey
        </Navbar.Brand>
      </Container>
    </Navbar>
  </div>
);

// here app catches the suspense from page in case translations are not yet loaded
function AppBar(props: any) {
  useEffect(() => {
    LoggerGroup.emit("Loaded");
  }, []);

  return (
    <Suspense fallback={<Loader />}>
      <Page {...props} />
    </Suspense>
  );
}

const mapStateToProps = (state: any) => ({
  user: state.user,
  cpp: state.cpp,
});

export default connect(mapStateToProps, {
  logoutUser,
  getCPP,
  setCPP,
  refreshCPP,
  clearSearchList,
  getCPPConfig,
})(AppBar);
